import React, { useContext, FC } from 'react';

import { useExternalLogos, LogoSlug, LogoLanguagesSet, LogoFile } from '@lucid/utils';
import { variant, ComponentsType, mqMin } from '@lucid/theme';
import styled from '@emotion/styled';
// import { GatsbyImage } from 'gatsby-plugin-image';

import Image from '../image/image';
import LayoutContext from '../context/layout-context';
import Box from '../box/box';
import { I18n } from '../../types/types';

export interface LogoSectionProps {
  /** Allows the component to get additional styles. */
  className?: string;
  /** Sets the logo height */
  height?: number;
  /** Sets which logos should be displayed */
  logos?: LogoSlug[] | LogoLanguagesSet;
  /** Sets the color of the logos */
  logosColor?: ComponentsType['LogoSection']['Colors'];
  /** The available variants for the LogoSection component. */
  variant?: ComponentsType['LogoSection']['Layouts'];
}

const StyledLogoSection = styled.ul(
  variant({
    prop: 'logosColor',
    scale: 'logoSection.colors',
    variants: {
      defaultVariant: {},
    },
  }),
  variant({
    scale: 'logoSection.layouts',
    variants: {
      defaultVariant: {},
    },
  }),
);

const LogoSection: FC<LogoSectionProps> = ({
  className,
  logos = ['hp', 'amazon', 'nbc', 'google', 'atlassian'],
  logosColor = 'original',
  height = 70,
  variant = 'oneLine',
}) => {
  const {
    language: { shortLangPrefix },
  } = useContext(LayoutContext);

  if (logos && (logos as LogoLanguagesSet)[shortLangPrefix as I18n.AvailableLanguages]) {
    // logos is a LogoLanguagesSet and has logos for the current language
    logos = (logos as LogoLanguagesSet)[shortLangPrefix as I18n.AvailableLanguages] as LogoSlug[];
  }
  if ((logos as LogoLanguagesSet).en) {
    // logos is a LogoLanguagesSet but needs to use english as fallback
    logos = (logos as LogoLanguagesSet).en as LogoSlug[];
  }

  const selectedLogos = useExternalLogos(logos as LogoSlug[]) as LogoFile[];

  const sections = [];
  for (let i = 0; i < (variant.indexOf('Carousel') >= 0 ? 2 : 1); i++) {
    sections.push(
      <StyledLogoSection
        key={`logo-section-${i}`}
        aria-hidden={!!i || undefined}
        css={{
          animationDuration: `calc(7s * ${selectedLogos.length})`,
        }}
        // eslint-disable-next-line react/jsx-sort-props
        className={className}
        logosColor={logosColor}
        variant={variant}
      >
        {selectedLogos?.map(
          (
            {
              node: {
                publicURL,
                childImageSharp,
                fields: { slug, alt },
              },
            },
            index,
          ) => (
            <li
              key={`logo-${slug}-${index}-${i}`}
              css={{
                textAlign: 'center',
                height: `calc((${height}px / 70) * 90)`, // helps size square logos more evenly with long logos on small devices
                [mqMin.tabletSmall]: {
                  height: height,
                },
              }}
            >
              {childImageSharp ? (
                // <GatsbyImage
                //   alt={alt}
                //   css={{
                //     width: '100%',
                //     height: '100%'
                //   }}
                //   image={childImageSharp.gatsbyImageData}
                //   loading="lazy"
                //   objectFit="contain"
                // />
                <Image
                  css={{
                    objectFit: 'contain',
                    width: '100%',
                    height: '100%',
                  }}
                  imageAlt={alt}
                  imageSrc={publicURL}
                  loading="lazy"
                />
              ) : (
                <Image
                  css={{
                    objectFit: 'contain',
                    width: '100%',
                    height: '100%',
                  }}
                  imageAlt={alt}
                  imageSrc={publicURL}
                  loading="lazy"
                />
              )}
            </li>
          ),
        )}
      </StyledLogoSection>,
    );
  }

  return <Box css={{ position: 'relative' }}>{sections}</Box>;
};

export { LogoSection };
export default LogoSection;
